import { QueryClient } from '@tanstack/react-query';
import { postSessionReportResolution, revokeSessionReportResolution } from './api';
import { describeResolutionError } from './errors';
import { isSameCoachSnapshot } from './collect';
import { SESSION_REPORT_SHEETS } from './labels';
import { sessionReportKeys } from './keys';
import { SessionReportMembership, SessionReportResolveBasis, SessionReportResolutionRequest, SessionReportResolutionResult, SessionReportResult } from './types';

export const RESOLUTION_CONFIRM_TEXT = '처리 완료로 두면 이 회원은 손볼 목록에서 빠져요. 수업이 더 잡히거나 총회차가 바뀌어 숫자가 달라지면 손볼 목록에 다시 보여 드려요.';

const newRequestId = () => {
    const bytes = new Uint8Array(16);
    if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
    else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export type ResolutionTask = {
    key: string;
    name: string;
    body?: SessionReportResolutionRequest;
    resolutionId?: string;
    previousResolutionId?: string;
    done: boolean;
    error?: string;
    changed?: boolean;
};

// 실패·응답 유실 요청은 창을 닫았다 열어도 유지한다. 로그아웃의 queryClient.clear()로 함께 지운다.
const requestKey = (key: string) => ['sessionReportResolutionRequest', key] as const;

export const hasResolveBasis = (m: SessionReportMembership): m is SessionReportMembership & { resolveBasis: SessionReportResolveBasis } =>
    !!m.resolveBasis && typeof m.resolveBasis.signature === 'string' && m.resolveBasis.signature.length > 0
    && Number.isInteger(m.resolveBasis.endRemaining) && typeof m.resolveBasis.carryKey === 'string'
    && /^c1:[0-9a-f]{64}$/.test(m.resolveBasis.carryKey);

export const matchesResolutionBasis = (m: SessionReportMembership, body: SessionReportResolutionRequest) =>
    hasResolveBasis(m) && m.resolveBasis.signature === body.expectedSignature && m.resolveBasis.endRemaining === body.endRemaining
    && m.resolveBasis.carryKey === body.carryKey;

export const createResolutionTasks = (client: QueryClient, centerId: string, yearMonth: string, memberships: SessionReportMembership[]): ResolutionTask[] => {
    const seen = new Set<string>();
    return memberships.filter(hasResolveBasis).filter((m) => {
        if (m.resolution === undefined || m.resolution?.matches === true || seen.has(m.membershipId)) return false;
        seen.add(m.membershipId);
        return true;
    }).map((m) => {
        const { signature, endRemaining, carryKey } = m.resolveBasis;
        const key = JSON.stringify([centerId, m.membershipId, yearMonth, signature, endRemaining, carryKey, m.resolution?.id ?? '']);
        const body = client.getQueryData<SessionReportResolutionRequest>(requestKey(key)) ?? {
            requestId: newRequestId(), membershipId: m.membershipId, yearMonth, expectedSignature: signature, endRemaining, carryKey,
        };
        client.getQueryCache().build(client, { queryKey: requestKey(key), cacheTime: Infinity }).setData(body);
        return { key, name: m.name, body, previousResolutionId: m.resolution?.id, done: false };
    });
};

export const createRevokeTasks = (memberships: SessionReportMembership[]): ResolutionTask[] => {
    const seen = new Set<string>();
    return memberships.flatMap((m) => {
        if (!m.resolution || m.resolution.matches !== true || seen.has(m.resolution.id)) return [];
        seen.add(m.resolution.id);
        return [{ key: m.resolution.id, name: m.name, resolutionId: m.resolution.id, done: false }];
    });
};

/** 각 장을 끝까지 순서대로 보낸다. 부분 실패 재시도에는 성공한 장을 건너뛴다. */
export const runResolutionTasks = async (client: QueryClient, centerId: string, tasks: ResolutionTask[]) => {
    // 계정 전환으로 캐시가 지워지면 남은 POST와 뒤따르는 조회를 멈춘다.
    const runKey = ['sessionReportResolutionRun', newRequestId()];
    client.getQueryCache().build(client, { queryKey: runKey, cacheTime: Infinity }).setData(true);
    try {
        for (const task of tasks) {
            if (!client.getQueryState(runKey)) return false;
            if (task.done) continue;
            let result: SessionReportResolutionResult;
            try {
                result = task.body ? await postSessionReportResolution(centerId, task.body)
                    : await revokeSessionReportResolution(centerId, task.resolutionId!);
            } catch {
                result = { status: 'error', message: describeResolutionError() };
            }
            if (!client.getQueryState(runKey)) return false;
            task.done = result.status === 'ok' || result.status === 'changed';
            task.changed = result.status === 'changed';
            task.error = result.status === 'error' ? result.message : undefined;
            if (task.changed && task.body) client.removeQueries({ queryKey: requestKey(task.key), exact: true });
            if (result.status === 'error' && result.renewRequest && task.body) {
                // 서버가 다른 대상에 쓰인 키라고 확정했다. 다음 시도는 새 요청이다.
                task.body = { ...task.body, requestId: newRequestId() };
                client.setQueryData(requestKey(task.key), task.body);
            }
        }
        return true;
    } finally {
        client.removeQueries({ queryKey: runKey, exact: true });
    }
};

type ResolutionScope = { centerId: string; coachId: string; yearMonth: string; ghostDays: number };
export const resolutionDisplayKey = (params: ResolutionScope, rowKey: string) =>
    [...sessionReportKeys.all, 'resolutionDisplay', params.centerId, params.yearMonth, params.ghostDays, params.coachId, rowKey] as const;
const refreshKey = (params: ResolutionScope) =>
    [...sessionReportKeys.all, 'resolutionRefresh', params.centerId, params.yearMonth, params.ghostDays, params.coachId] as const;

/** 완료 처리의 요약 조회와 기존 자동 동기화가 같은 응답을 중복 조회하지 않게 해요. */
export const hasResolutionRefresh = (client: QueryClient, params: ResolutionScope, detailUpdatedAt?: number) => {
    const state = client.getQueryData<{ pending: boolean; detailUpdatedAt?: number }>(refreshKey(params));
    return !!state && (state.pending || (detailUpdatedAt !== undefined && state.detailUpdatedAt === detailUpdatedAt));
};

/** 해당 강사와 요약만 무효화해요. 진행 중인 화면·엑셀 조회는 취소하지 않아요. */
export const refreshAfterResolution = async (client: QueryClient, params: {
    centerId: string; coachId: string; yearMonth: string; ghostDays: number;
}) => {
    const filters = {
        queryKey: [...sessionReportKeys.all, 'report'],
        predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[2] === params.centerId
            && (query.queryKey[5] === params.coachId || query.queryKey[5] === ''),
    };
    const detailKey = sessionReportKeys.report(params.centerId, params.yearMonth, params.ghostDays, params.coachId);
    const summaryKey = sessionReportKeys.report(params.centerId, params.yearMonth, params.ghostDays);
    client.setQueryData(refreshKey(params), { pending: true });
    try {
        // 모든 활성 요청이 끝난 뒤 아래에서 각각의 성공 여부를 확인해요.
        await client.invalidateQueries({ ...filters, refetchType: 'active' }, { cancelRefetch: false });
    } finally {
        // 계정 전환으로 비워진 캐시를 되살리지 않아요.
        if (client.getQueryState(refreshKey(params))) client.setQueryData(refreshKey(params), {
            pending: false, detailUpdatedAt: client.getQueryState(detailKey)?.dataUpdatedAt,
        });
    }
    const states = [detailKey, summaryKey].map((key) => client.getQueryState<SessionReportResult>(key));
    if (states.some((state) => state?.status !== 'success' || state.data?.status !== 'ok' || state.isInvalidated)) {
        throw new Error('처리 뒤 자료를 다시 불러오지 못했어요.');
    }
    const detail = states[0]!.data!;
    const summary = states[1]!.data!;
    if (detail.status === 'ok' && summary.status === 'ok') {
        const detailCoach = detail.report.coaches.find((coach) => coach.coachId === params.coachId);
        const summaryCoach = summary.report.coaches.find((coach) => coach.coachId === params.coachId);
        // 저장 전에 시작한 조회를 공유했을 수 있어요. 서로 다른 시점의 응답으로 완료를 확정하지 않아요.
        if ([detail.report, summary.report].some((report) => report.yearMonth !== params.yearMonth || report.ghostDays !== params.ghostDays)
            || !summary.report.centerTotals || !detailCoach || !summaryCoach
            || !SESSION_REPORT_SHEETS.every(({ key }) => Array.isArray(detailCoach.sheets[key]?.rows))
            || !isSameCoachSnapshot(summaryCoach, detailCoach)) throw new Error('숫자 반영을 기다리고 있어요.');
        const memberships = detail.report.coaches.flatMap((coach) => Object.values(coach.sheets).flatMap((sheet) =>
            (sheet.rows ?? []).flatMap((row) => row.memberships)));
        if (memberships.some((m) => m.resolveBasis && m.resolution === undefined)) {
            throw new Error('처리 기록을 다시 불러오지 못했어요.');
        }
        client.getQueryCache().findAll({ queryKey: ['sessionReportResolutionRequest'] }).forEach((query) => {
            const [center, membershipId, yearMonth, , , , previousId] = JSON.parse(String(query.queryKey[1]));
            if (center !== params.centerId || yearMonth !== params.yearMonth) return;
            const membership = memberships.find((m) => m.membershipId === membershipId);
            if (!membership || membership.resolution === undefined) return;
            // 같은 달의 새 기록 또는 기준 변경을 확인한 뒤에만 이전 요청 번호를 버려요.
            if (membership.resolution?.matches === true || !matchesResolutionBasis(membership, query.state.data as SessionReportResolutionRequest)
                || (membership.resolution?.id ?? '') !== previousId) client.removeQueries({ queryKey: query.queryKey, exact: true });
        });
    }
};
